/**
 * Thin wrapper around the Office JavaScript API (office.js).
 *
 * Every call degrades gracefully when office.js is absent (dev in a plain
 * browser), returning null/empty instead of throwing, so the UI stays usable
 * while iterating on the React layer.
 */
import type { FeatureId } from "@/types";

declare global {
  interface Window {
    Office?: typeof Office;
  }
}

export function hasOfficeHost(): boolean {
  return typeof window !== "undefined" && Boolean(window.Office);
}

/** Initialize the Office add-in and run onReady. Resolves immediately outside Outlook. */
export function initOffice(onReady?: () => void): void {
  if (!hasOfficeHost() || !window.Office) {
    onReady?.();
    return;
  }
  window.Office.onReady((info) => {
    void info;
    onReady?.();
  });
}

function currentItem(): Office.MessageRead | Office.MessageCompose | null {
  if (!hasOfficeHost() || !window.Office?.context?.mailbox) return null;
  return window.Office.context.mailbox.item ?? null;
}

/** True when a given Mailbox requirement set is supported by the host. */
export function supportsMailboxSet(version: string): boolean {
  try {
    return Boolean(window.Office?.context?.requirements?.isSetSupported("Mailbox", version));
  } catch {
    return false;
  }
}

/** True when the add-in was opened on a message being composed. */
export function isComposeMode(): boolean {
  const item = currentItem();
  if (!item) return false;
  try {
    return (
      item.itemType === Office.MailboxEnums.ItemType.Message &&
      typeof (item as Office.MessageCompose).body.setSelectedDataAsync === "function"
    );
  } catch {
    return false;
  }
}

/** Read the entire body of the currently open email as plain text. Returns "" if unavailable. */
export async function readBody(): Promise<string> {
  const item = currentItem();
  if (!item || !("body" in item)) return "";
  return new Promise((resolve) => {
    (item as Office.MessageRead).body.getAsync(
      Office.CoercionType.Text,
      (result: Office.AsyncResult<string>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value ?? "");
        } else {
          resolve("");
        }
      },
    );
  });
}

/** Read subject + sender + recipients as a small metadata block. */
export async function readHeaders(): Promise<{ subject: string; from: string; to: string }> {
  const item = currentItem();
  if (!item) return { subject: "", from: "", to: "" };
  const subject = (item as Office.MessageRead).subject ?? "";
  const fromField = (item as Office.MessageRead).from?.emailAddress ?? "";
  let to = "";
  try {
    const toRecipients = (item as Office.MessageRead).to;
    if (Array.isArray(toRecipients)) {
      to = toRecipients.map((r) => r.emailAddress).join(", ");
    }
  } catch {
    /* not available in this mode */
  }
  return { subject, from: fromField, to };
}

/**
 * Load the currently-open email: subject, from, to, and full body (plain text).
 * Works in both read and compose mode. Outside Outlook returns empty fields.
 */
export async function loadCurrentEmail(): Promise<{
  subject: string;
  from: string;
  to: string;
  body: string;
}> {
  const [headers, body] = await Promise.all([readHeaders(), readBody()]);
  return { ...headers, body };
}

/** Replace the body of the open compose message with `text`. No-op outside Outlook. */
export async function setBody(text: string): Promise<void> {
  const item = currentItem();
  if (!item || !("body" in item)) return;
  return new Promise((resolve, reject) => {
    (item as Office.MessageCompose).body.setAsync(
      text,
      { coercionType: Office.CoercionType.Text },
      (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) resolve();
        else reject(new Error(result.error?.message ?? "setBody failed"));
      },
    );
  });
}

/** Insert text into the compose body at the cursor. */
export async function insertText(text: string): Promise<void> {
  const item = currentItem();
  if (!item || !("body" in item)) return;
  return new Promise((resolve, reject) => {
    (item as Office.MessageCompose).body.setSelectedDataAsync(
      text,
      { coercionType: Office.CoercionType.Text },
      (result: Office.AsyncResult<void>) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) resolve();
        else reject(new Error(result.error?.message ?? "insertText failed"));
      },
    );
  });
}

/**
 * Hide the task pane. Requires the shared runtime (Office.addin namespace,
 * SharedRuntime 1.1) — available in the unified manifest or an XML manifest
 * configured for shared runtime. In a classic per-taskpane XML manifest this
 * is unavailable, so we return a hint telling the user to use the built-in ×.
 */
export async function hidePanel(): Promise<{ ok: boolean; hint?: string }> {
  const O = window.Office as typeof Office | undefined;
  const addin = (O as unknown as { addin?: { hide?: () => Promise<void> } })?.addin;
  if (addin && typeof addin.hide === "function") {
    try {
      await addin.hide();
      return { ok: true };
    } catch (e) {
      return { ok: false, hint: String(e) };
    }
  }
  return {
    ok: false,
    hint: "Programmatic hide needs the shared runtime (unified manifest). For now, close with the × at the top corner of the task pane.",
  };
}

/** The Office theme (Mailbox 1.14). Null when unsupported (older Outlook / outside Outlook). */
export function getOfficeTheme(): Office.OfficeTheme | null {
  try {
    const t = (window.Office?.context as { officeTheme?: Office.OfficeTheme })?.officeTheme;
    if (t && typeof t.bodyBackgroundColor === "string") return t;
    return null;
  } catch {
    return null;
  }
}

/**
 * Subscribe to Office theme changes. Returns a cleanup function. No-op when
 * the OfficeThemeChanged event is unavailable.
 */
export function onOfficeThemeChanged(cb: (theme: Office.OfficeTheme) => void): () => void {
  if (!hasOfficeHost() || !window.Office?.context?.mailbox) return () => {};
  try {
    const mailbox = window.Office.context.mailbox as unknown as {
      addHandlerAsync: (type: Office.EventType, handler: (arg: Office.OfficeThemeChangedEventArgs) => void) => void;
      removeHandlerAsync: (type: Office.EventType, handler: (arg: Office.OfficeThemeChangedEventArgs) => void) => void;
    };
    const handler = (arg: Office.OfficeThemeChangedEventArgs) => cb(arg.officeTheme);
    mailbox.addHandlerAsync(Office.EventType.OfficeThemeChanged, handler);
    return () => {
      try {
        mailbox.removeHandlerAsync(Office.EventType.OfficeThemeChanged, handler);
      } catch {
        /* best-effort */
      }
    };
  } catch {
    return () => {};
  }
}

/** Maps a feature to whether it needs compose (write) access. */
export function featureNeedsCompose(feature: FeatureId): boolean {
  return feature === "draft" || feature === "tone" || feature === "proofread";
}
